import React from 'react';
import PropTypes from 'prop-types';
import Downshift from 'downshift';
import Fuse from 'fuse.js';
import diacritics from 'diacritics';
import RowVirtualizerDynamic from './RowVirtualizerDynamic';
import getStyles from '@front10/helpers/dist/UseStyles';
import { Context } from '@front10/helpers/dist/Context';
import { getUuid } from '@front10/helpers/dist/uuid';
import { clientHeight } from '../../../helpers/utils';
import { isSSR } from '@front10/utils';
import LocationSelectorInput from './LocationSelectorInput';
import LocationSelectorMenu from './LocationSelectorMenu';
import LocationSelectorMenuItem from './LocationSelectorMenuItem';
import LocationSelectorNoResult from './LocationSelectorNoResult';
import LocationSelectorGroupHeader from './LocationSelectorGroupHeader';
import LocationSelectorSecondarySearch from './LocationSelectorSecondarySearch';
import Box from '../../elements/Box';
import Button from '../../elements/Button';
import VisuallyHidden from '@reach/visually-hidden';
import Icon from '../../elements/Icon';
import { strFormat } from '../../../helpers/utils';
import ReactFocusLock from 'react-focus-lock';

const regexMap = {
  onlyNumbers: '[0-9, ]',
  onlyLetters: '[a-zA-Z, ]',
  lettersAndNumbers: '[a-zA-Z, 0-9]'
};
const KEY_TAB = 9;
const KEY_ENTER = 13;
const KEY_ESC = 27;
const KEY_ARROW_DOWN = 40;

class LocationSelector extends React.Component {
  constructor(props) {
    super(props);
    const selectedItem = {};
    selectedItem[props.displayField] = '';
    selectedItem[props.valueField] = null;
    this.downshift = null;
    this.wasOpenSelector = React.createRef(false);
    this.locationSelectorInputRef = React.createRef(false);
    this.state = {
      selectedItem,
      showEmptyFilterMessage: false,
      inputValue: selectedItem[props.displayField] ? `${selectedItem[props.displayField]}` : '',
      inputValueSearch: selectedItem[props.displayField],
      clearable: props.clearable,
      wasOpenFirstTime: false,
      inputValueSearchSecondary: '',
      enabledAccessibilityText: false,
      firstResultAnt: '',
      manuallyOpened: false,
      dropDownHeight: props.isMobile
        ? clientHeight() - props.virtualListHeightSubtraction
        : Math.min(
            this.getOptions(selectedItem[props.displayField], '')?.length * props.itemHeight + 50,
            props.dropDownMaxHeight
          )
    };
    this.highlightedIndex = this.props.openOnFocus ? null : 0;
    this.labelId = `locationSelectorLabel-${getUuid()}`;
  }

  removeA11YMessage = () => {
    setTimeout(() => {
      this.setState({ enabledAccessibilityText: false });
    }, 150);
  };

  componentDidMount = () => {
    const {
      defaultValue,
      displayField,
      valueField,
      clearable,
      isMobile,
      charactersAllowed,
      virtualListHeightSubtraction,
      itemHeight,
      dropDownMaxHeight
    } = this.props;
    const selectedItem =
      this.props.toggleButtonLabels !== undefined
        ? this.props.toggleButtonLabels
        : this.findSelectedItem(defaultValue);
    let newInputValue;
    if (this.props.showToggleButtonLabels || this.props.showToggleButtonLabels === undefined) {
      newInputValue = selectedItem[displayField];
    } else {
      if (this.props.selectedItems && this.props.selectedItems.length !== 0) {
        if (this.props.isConfirmClearAction === false) {
          if (this.props.copySelectedItems.length === 0) {
            newInputValue = undefined;
          } else {
            newInputValue = selectedItem[displayField];
          }
        } else {
          newInputValue = selectedItem[displayField];
        }
      } else {
        if (this.props.isConfirmClearAction === false) {
          newInputValue = selectedItem[displayField];
        } else {
          newInputValue = undefined;
        }
      }
    }
    this.setState({
      selectedItem,
      inputValue: newInputValue,
      inputValueSearch: selectedItem[displayField],
      clearable: !!(defaultValue && selectedItem[valueField] && clearable),
      dropDownHeight: isMobile
        ? clientHeight() - virtualListHeightSubtraction
        : Math.min(
            this.getOptions(selectedItem[displayField], '')?.length * itemHeight + 50,
            dropDownMaxHeight
          )
    });
    this.buildValidationRegex(charactersAllowed);
    if (!isSSR() && isMobile) {
      window.addEventListener('resize', this.onResize);
    }
  };

  componentWillUnmount = () => {
    const { isMobile } = this.props;
    if (!isSSR() && isMobile) {
      window.removeEventListener('resize', this.onResize);
    }
  };

  onResize = () => {
    const list = document.querySelector('.location-selector-menu-container ol');
    if (list) {
      list.style.height = `${clientHeight()}px`;
      this.setState({ dropDownHeight: clientHeight() });
    }
  };

  // Only runs on mobile call from PropertyFilter
  update = (value) => {
    const { defaultValue, displayField, valueField, clearable } = this.props;
    const selectedItem =
      this.props.toggleButtonLabels !== undefined
        ? this.props.toggleButtonLabels
        : this.findSelectedItem(defaultValue);
    const inputValue = selectedItem[displayField]
      ? `${selectedItem[displayField]}`
      : selectedItem[displayField];
    this.setState({
      selectedItem,
      inputValue: inputValue.trim(),
      inputValueSearch: selectedItem[displayField],
      clearable: !!(defaultValue && selectedItem[valueField] && clearable)
    });
  };

  updateState = (item) => {
    const { defaultValue, displayField, valueField, clearable } = this.props;
    const inputValue = item[displayField] ? `${item[displayField]}` : item[displayField];
    this.setState({
      selectedItem: item,
      inputValue: inputValue.trim(),
      inputValueSearch: item[displayField],
      clearable: !!(defaultValue && item[valueField] && clearable)
    });
  };

  componentDidUpdate = (prevProps, prevState) => {
    this.onUpdate(prevProps);

    if (
      this.props.minLength &&
      prevState?.inputValue !== undefined &&
      this.state.inputValue !== prevState.inputValue &&
      this.downshift?.state?.isOpen &&
      this.state.inputValue.length < this.props.minLength &&
      this.state.manuallyOpened !== false
    ) {
      this.downshift.closeMenu();
      this.setState({ manuallyOpened: false });
    }

    if (
      this.props.allowVariableHighlightedIndex === true &&
      this.downshift?.state?.isOpen === false
    ) {
      if (this.state?.selectedItem !== undefined) {
        const index = this.props?.options?.findIndex(
          (e) => e?.value === this.state?.selectedItem?.value
        );
        this.downshift?.setState({ highlightedIndex: index });
      }
    }
  };

  onUpdate = (prevProps) => {
    const {
      options,
      defaultValue,
      displayField,
      defaultIsOpen,
      valueField,
      clearable,
      charactersAllowed,
      isMobile,
      itemHeight,
      dropDownMaxHeight
    } = this.props;
    if (options !== prevProps.options || defaultValue !== prevProps.defaultValue) {
      const selectedItem =
        this.props.toggleButtonLabels !== undefined
          ? this.props.toggleButtonLabels
          : this.findSelectedItem(defaultValue);
      let newInputValue;
      if (this.props.showToggleButtonLabels || this.props.showToggleButtonLabels === undefined) {
        newInputValue = selectedItem[displayField];
      } else {
        if (this.props.selectedItems && this.props.selectedItems.length !== 0) {
          if (this.props.isConfirmClearAction === false) {
            if (this.props.copySelectedItems.length === 0) {
              newInputValue = undefined;
            } else {
              newInputValue = selectedItem[displayField];
            }
          } else {
            newInputValue = selectedItem[displayField];
          }
        } else {
          if (this.props.isConfirmClearAction === false) {
            newInputValue = selectedItem[displayField];
          } else {
            newInputValue = undefined;
          }
        }
      }
      if (!isMobile) {
        this.setState({
          dropDownHeight: Math.min(
            this.getOptions(selectedItem[displayField], '')?.length * itemHeight + 50,
            dropDownMaxHeight
          )
        });
      }
      if ((selectedItem[displayField] && selectedItem[valueField]) || !defaultValue) {
        this.setState({
          selectedItem,
          inputValue: newInputValue,
          inputValueSearch: selectedItem[displayField],
          clearable: !!(defaultValue && selectedItem[valueField] && clearable)
        });
      }
    }
    const hasDefaultValue = !(this.props.defaultValue === '' || this.props.defaultValue === null);
    if (
      (!defaultIsOpen && defaultIsOpen !== prevProps.defaultIsOpen) ||
      (defaultIsOpen && hasDefaultValue && prevProps.defaultValue !== this.props.defaultValue)
    ) {
      this.onCloseMenu();
    }
    if (charactersAllowed !== prevProps.charactersAllowed) {
      this.buildValidationRegex(charactersAllowed);
    }
  };

  onStateChange = (changes, helpers) => {
    if (changes.isOpen) {
      this.setState({ wasOpenFirstTime: true, inputValueSearchSecondary: '' });
      if (this.props.allowVariableHighlightedIndex !== true) {
        if (this.props.openOnFocus !== true || this.state.selectedItem?.value !== '') {
          if (this.props.isMobile) {
            setTimeout(() => this.downshift?.setState({ highlightedIndex: 0 }), 20);
          } else {
            this.downshift?.setState({ highlightedIndex: 0 });
          }
        }
      } else {
        const filter = this.getOptions(
          this.state.inputValueSearch,
          this.state.inputValueSearchSecondary
        );
        const selectedIndex = filter.findIndex(
          (item) => item.value === helpers.selectedItem?.value
        );
        const highlightIndex = selectedIndex >= 0 ? selectedIndex : 0;
        helpers.setState({ highlightedIndex: highlightIndex });
      }
    } else if (changes.highlightedIndex !== 0) {
      this.setState({ wasOpenFirstTime: false });
    }

    if (changes.selectedItem) {
      // && Object.prototype.hasOwnProperty.call(changes, 'highlightedIndex')
      const { onChange, clearable, displayField, displayFilterEmptyMessage = false } = this.props;
      const { selectedItem } = changes;
      this.setState({
        selectedItem,
        clearable,
        inputValue: selectedItem[displayField]
          ? `${selectedItem[displayField]}`
          : selectedItem[displayField],
        inputValueSearch: selectedItem[displayField]
      });
      if (displayFilterEmptyMessage) {
        this.setState({ showEmptyFilterMessage: false });
      }
      if (
        this.props.execOnChangeV2 === undefined ||
        this.props.execOnChangeV2 === null ||
        this.props.execOnChangeV2 === false
      ) {
        onChange(selectedItem);
      } else {
        this.props.onChangeV2(selectedItem);
      }

      if (this.props.isMobile && this.props.forceBlur) {
        this.locationSelectorInputRef?.current?.selectorInputRef?.current?.inputRef?.current?.blur?.();
      }
    }
    if (changes.isOpen === false && this.props.multipleSelection && this.props.isMobile) {
      const { onChange } = this.props;
      const { selectedItem } = changes;
      onChange(selectedItem);
    }

    // Disabled: This logic was clearing the input when dropdown closed,
    // causing the input to be completely erased instead of preserving user's value.
    // if (
    //   changes.isOpen === false &&
    //   inputValue !== '' &&
    //   !selectedItem[displayField] &&
    //   !selectedItem[valueField] &&
    //   !changes.selectedItem
    // ) {
    //   this.setState({ inputValue: '', inputValueSearch: '' });
    // }
    this.setState({ firstResultAnt: '' });
  };

  onClear = () => {
    const { onClear, displayField, valueField } = this.props;
    const selectedItem = {};
    selectedItem[displayField] = '';
    selectedItem[valueField] = null;
    this.setState({
      selectedItem,
      clearable: false,
      inputValue: '',
      inputValueSearch: ''
    });

    if (typeof document !== 'undefined' && this.props.isMobile) {
      setTimeout(() => {
        const input = this.locationSelectorInputRef?.current?.selectorInputRef?.current?.inputRef
          ?.current;
        input?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        window.scrollTo({
          top: input?.offsetTop - 50,
          behavior: 'smooth'
        });
      }, 100);
    }

    onClear();
  };

  onCloseMenu = () => {
    this.downshift.closeMenu();
    this.setState({ manuallyOpened: false });
    this.removeA11YMessage();
  };

  onOpenMenu = () => {
    const { onOpen } = this.props;
    this.downshift.openMenu();
    this.setState({ manuallyOpened: true, enabledAccessibilityText: true });
    onOpen();
  };

  onFocus = () => {
    const { openOnFocus, onFocus, isDirty } = this.props;
    const input = this.locationSelectorInputRef?.current?.selectorInputRef?.current?.inputRef
      ?.current;
    if (input?.getAttribute('data-focus') !== 'no-focus') {
      // Move scroll to input when focus on input and is mobile
      // if (typeof document !== 'undefined' && this.props.isMobile) {
      //   input.addEventListener('focus', function (evt) {
      //     setTimeout(() => {
      //       input.scrollIntoView({ behavior: 'smooth', block: 'center' });
      //       // window.scrollTo(0, evt.target.offsetTop - 50); // FIXME: This is another way to scroll to input for iOS issue
      //     }, 0);
      //   });
      // }

      if (openOnFocus) {
        if (!isDirty) {
          this.downshift.openMenu();
        }
      }

      onFocus();
    } else {
      input?.removeAttribute('data-focus');
    }
  };

  findSelectedItem = (value) => {
    const { translate } = this.context;
    const {
      valueField,
      displayField,
      options,
      multipleSelection,
      multipleSelectionGroupLabel,
      disabled,
      placeholder,
      selectedItems,
      labelKeySeparatorMultiSelection
    } = this.props;
    if (!multipleSelection) {
      const item = options.find((i) => {
        return i[valueField] === value;
      });
      const defaultItem = {
        [displayField]: '',
        [valueField]: ''
      };
      return item || defaultItem;
    } else {
      let defaultItem;
      if (disabled || selectedItems.length === 0) {
        defaultItem = {
          [displayField]: placeholder,
          [valueField]: placeholder
        };
      } else {
        if (options.length > 1) {
          if (selectedItems.length === this.props.options.length) {
            defaultItem = {
              [displayField]: translate('cmp-filter-all-items-selected', 'All items selected'),
              [valueField]: translate('cmp-filter-all-items-selected', 'All items selected')
            };
          } else {
            if (selectedItems.length === 1) {
              defaultItem = {
                [displayField]: selectedItems[0].label,
                [valueField]: selectedItems[0].label
              };
            } else {
              if (multipleSelectionGroupLabel) {
                defaultItem = {
                  [displayField]: translate(
                    `cmp-filter-${selectedItems.length}-items-selected`,
                    `Multiple Airlines ( ${selectedItems.length} Selected)`
                  ),
                  [valueField]: translate(
                    `cmp-filter-${selectedItems.length}-items-selected`,
                    `Multiple Airlines ( ${selectedItems.length} Selected)`
                  )
                };
              } else {
                defaultItem = {
                  [displayField]: selectedItems
                    .map((i) => i.label)
                    .sort()
                    .join(translate(labelKeySeparatorMultiSelection, ', ')),
                  [valueField]: translate(
                    `cmp-filter-${selectedItems.length}-items-selected`,
                    `Multiple Airlines ( ${selectedItems.length} Selected)`
                  )
                };
              }
            }
          }
        } else if (selectedItems.length === 1) {
          defaultItem = {
            [displayField]: selectedItems[0].label,
            [valueField]: selectedItems[0].label
          };
        } else {
          defaultItem = {
            [displayField]: translate('cmp-filter-no-item-selected', 'No item selected'),
            [valueField]: translate('cmp-filter-no-item-selected', 'No item selected')
          };
        }
      }
      return defaultItem;
    }
  };

  handleInputValue = ($event) => {
    const { displayFilterEmptyMessage = false } = this.props;
    if (displayFilterEmptyMessage) {
      if ($event.target.value === '') {
        this.setState({ showEmptyFilterMessage: true });
      } else {
        if (this.state.showEmptyFilterMessage) {
          this.setState({ showEmptyFilterMessage: false });
        }
      }
    }
    if ($event.target.value === '' && this.props.isMobile) {
      this.props.onClear();
    }
    const {
      onInputChange,
      isMobile,
      itemHeight,
      dropDownMaxHeight,
      selectOptionByDefault
    } = this.props;
    const inputValueSearch = $event.target.value.split(' (')[0].toString().trim();
    const inputValue = $event.target.value;
    const newState = { inputValue, inputValueSearch };

    if (selectOptionByDefault) {
      this.setState(newState, () => {
        if (!this.props.minLength || inputValue.length >= this.props.minLength) {
          this.onOpenMenu();
        } else {
          this.setState({ manuallyOpened: false });
        }
      });
    } else {
      this.setState(newState);
    }
    onInputChange(inputValue);
    this.downshift.setState({ highlightedIndex: 0 });
    const filter = this.getOptions(
      this.state.inputValueSearch,
      this.state.inputValueSearchSecondary
    );

    if (!isMobile) {
      this.setState({
        dropDownHeight: Math.min(filter?.length * itemHeight + 50, dropDownMaxHeight)
      });
    }

    filter.length > 0
      ? this.setState({ firstResultAnt: filter[0].iataCode })
      : this.setState({ firstResultAnt: '' });
  };

  scrollIntoView = (index) => {
    if (typeof document !== 'undefined') {
      const element = document.getElementById(`${this.downshift.id}-item-${index}`);
      if (element && element.parentNode) {
        element.parentNode.scrollTop = element.offsetTop;
      }
    }
  };

  itemRenderer = (item) => {
    const { itemRenderer, displayField } = this.props;
    if (itemRenderer) {
      return itemRenderer(item, this.props);
    }
    if (typeof item === 'string') {
      return item;
    }
    if (item) {
      return item[displayField];
    }
    return '';
  };
  stateReducer = (state, changes) => {
    // this prevents the menu from being closed when the user
    // lost the focus
    const { forceOpenDesktopInMobile } = this.props;
    if (this.props.multipleSelection) {
      if (this.props.notifyState) {
        if (changes.isOpen !== undefined) {
          this.props.notifyState(changes.isOpen);
        }
      }
      //(this.props.isMobile && this.props.defaultIsOpen) -> selected travel interest in "mobile"
      if (!this.props.isMobile || (this.props.isMobile && this.props.defaultIsOpen)) {
        if (changes.type === Downshift.stateChangeTypes.clickItem) {
          return {
            ...changes,
            highlightedIndex: state.highlightedIndex,
            isOpen: state.isOpen
          };
        }
      } else {
        if (changes.type === Downshift.stateChangeTypes.clickItem) {
          const { displayField, valueField, defaultValue, clearable } = this.props;
          const selectedItem =
            this.props.toggleButtonLabels !== undefined
              ? this.props.toggleButtonLabels
              : this.findSelectedItem(defaultValue);
          this.setState({
            selectedItem,
            inputValue: selectedItem[displayField]
              ? `${selectedItem[displayField]}`
              : selectedItem[displayField],
            inputValueSearch: selectedItem[displayField],
            clearable: !!(defaultValue && selectedItem[valueField] && clearable)
          });
          return {
            isOpen: state.isOpen
          };
        } else if (changes.isOpen) {
          return {
            isOpen: changes.isOpen,
            isMultiSelected: true
          };
        }
      }
    }
    if (this.props.allowVariableHighlightedIndex !== true) {
      if (
        changes.type === Downshift.stateChangeTypes.blurInput &&
        (this.props.isMobile === true || forceOpenDesktopInMobile)
      ) {
        return {
          ...changes,
          isOpen: state.isOpen
        };
      }
    } else {
      if (
        changes.type === Downshift.stateChangeTypes.blurInput &&
        (this.props.isMobile === true || forceOpenDesktopInMobile)
      ) {
        return {
          ...state
        };
      }
    }

    //returns the focus to the ToggleElement
    if (
      changes.type === Downshift.stateChangeTypes.mouseUp &&
      changes.isOpen === false &&
      this.props.inputItem.ToggleElement
    ) {
      if (this.props.slowCloseAction) {
        setTimeout(() => {
          this.locationSelectorInputRef?.current?.focusSelectorInput();
        }, 700);
      } else {
        this.locationSelectorInputRef?.current?.focusSelectorInput();
      }
    }
    //this.locationSelectorInputRef?.current?.focusSelectorInput();

    return changes;
  };

  onBlur = () => {
    const { onBlur, displayField, valueField, displayFilterEmptyMessage = false } = this.props;
    const { selectedItem } = this.state;

    if (
      selectedItem[displayField] &&
      selectedItem[valueField] != null &&
      selectedItem[valueField] !== ''
    ) {
      this.setState({
        inputValue: `${selectedItem[displayField]}`
      });
      if (displayFilterEmptyMessage) {
        this.setState({
          showEmptyFilterMessage: false
        });
      }
    }

    onBlur();
  };

  onKeyPress = (e) => {
    if (this.regex && !this.regex.test(e.key)) {
      e.preventDefault();
    }
  };
  onKeyDown = (e, filter, index, isOpen) => {
    const { minLength } = this.props;
    if (e.keyCode === KEY_ESC && isOpen) {
      e.preventDefault();
      e.stopPropagation();
      this.downshift.closeMenu();
      this.setState({ manuallyOpened: false });
      return;
    }

    if (e.keyCode === KEY_ARROW_DOWN && !isOpen) {
      e.preventDefault();
      this.onOpenMenu();
      return;
    }

    if (e.keyCode === KEY_TAB && isOpen && minLength <= this.state.inputValue.length) {
      const { displayField, valueField, defaultValue, clearable, onChange } = this.props;
      const selectedItem = filter[index];
      if (selectedItem !== undefined) {
        onChange(selectedItem);
        this.setState({
          selectedItem,
          inputValue: selectedItem[displayField]
            ? `${selectedItem[displayField]}`
            : selectedItem[displayField],
          inputValueSearch: selectedItem[displayField],
          clearable: !!(defaultValue && selectedItem[valueField] && clearable)
        });
        if (this.props.displayFilterEmptyMessage) {
          this.setState({ showEmptyFilterMessage: false });
        }
        this.setState({ firstResultAnt: '' });
      }
    }
    this.props.onKeyDown && this.props.onKeyDown(e, filter, index, isOpen);
  };

  buildValidationRegex = (charactersAllowed) => {
    if (charactersAllowed !== 'all') {
      const regexString = regexMap[charactersAllowed] || charactersAllowed;
      this.regex = new RegExp(regexString);
    }
  };

  onPaste = (event) => {
    const charactersAllowed = this.props.charactersAllowed;
    if (this.props.charactersAllowed !== 'all') {
      const value = (event.clipboardData || window.clipboardData).getData('text');
      const regex = new RegExp(`^${regexMap[charactersAllowed] || charactersAllowed}+$`);
      if (!regex.test(value)) {
        event.preventDefault();
      }
    }
  };

  getItemStateProps = (highlightedIndex, index, item, props) => {
    const { selectedItem } = this.state;
    const { valueField, options } = this.props;
    if (this.highlightedIndex && highlightedIndex) {
      if (
        this.highlightedIndex === 0 &&
        highlightedIndex === options.length - 1 &&
        options.length !== 2
      ) {
        this.downshift.setHighlightedIndex(0);
        return;
      } else if (
        this.highlightedIndex === options.length - 1 &&
        highlightedIndex === 0 &&
        options.length !== 2
      ) {
        this.downshift.setHighlightedIndex(options.length - 1);
        return;
      }
      this.highlightedIndex = highlightedIndex;
    }
    return {
      textColor: highlightedIndex === index ? props.hoverTextColor : props.textColor,
      bgColor: highlightedIndex === index ? props.hoverBgColor : props.bgColor,
      fontWeight:
        item[valueField] && selectedItem[valueField] === item[valueField] ? 'bold' : 'normal',
      className: highlightedIndex === index ? 'menu-item-selected' : 'menu-item-not-selected'
    };
  };

  focusSelectorInput = () => {
    if (this.locationSelectorInputRef) {
      if (this.props.slowCloseAction) {
        setTimeout(() => {
          this.locationSelectorInputRef?.current?.focusSelectorInput();
        }, 700);
      } else {
        this.locationSelectorInputRef?.current?.focusSelectorInput();
      }
    }
  };

  getOptions = (inputValueSearch, inputValueSearchSecondary) => {
    const { searchInside, options, selectMode, searchOptions } = this.props;
    // const { inputValueSearch, inputValueSearchSecondary } = this.state;
    let items = null;
    //only for the travel interest
    if (selectMode && Boolean(inputValueSearchSecondary)) {
      items = options.filter((option) => option.value !== 'any');
    }
    const fuse = new Fuse(
      Boolean(items) ? items : options.filter((item) => item.virtual !== true),
      {
        ...searchOptions,
        getFn: (obj, path) => {
          const fillingObject = (obj, searchOptions) => {
            const copyObject = { ...obj };
            let keys = searchOptions.keys;
            for (let index = 0; index < keys.length; index++) {
              const element = keys[index];
              let res = '';
              if (typeof element === 'string') {
                res = element.split('.');
              } else {
                if (typeof element === 'object' && Boolean(element.name)) {
                  res = element.name.split('.');
                }
              }
              let item = copyObject;
              for (let j = 0; j < res.length; j++) {
                const key = res[j];
                if (item !== undefined) {
                  item = item[key];
                  if (item === undefined) {
                    if (res.length === 1) {
                      //case as   "keys": ["iataCode"],
                      copyObject[key] = '';
                    } else {
                      //case as   "keys": ["city.name"],
                      let value = {};
                      for (let i = res.length - 1; i >= 0; i--) {
                        const element = res[i];
                        if (i === res.length - 1) {
                          value[element] = '';
                        } else if (i === 0) {
                          copyObject[element] = value;
                        } else {
                          value = { [element]: value };
                        }
                      }
                    }
                  }
                }
              }
            }
            return copyObject;
          };
          const newObject = fillingObject(obj, searchOptions);
          var value = Fuse.config.getFn(newObject, path);
          return diacritics.remove(value);
        }
      }
    );

    if (selectMode) {
      if (Boolean(inputValueSearchSecondary)) {
        return fuse.search(inputValueSearchSecondary).map((e) => e.item);
      } else {
        return options;
      }
    }

    if (searchInside && Boolean(inputValueSearch)) {
      return fuse.search(diacritics.remove(inputValueSearch)).map((e) => e.item);
    } else {
      return options;
    }
  };

  getLocationItemProps = (index, highlightedIndex, filter, getItemProps) => {
    const { grouped, displayField, valueField, listItem, groupItem } = this.props;
    const item = filter[index];
    const propStyles = item.category ? groupItem : listItem;

    return {
      attrs:
        grouped && !item.category
          ? {
              'aria-selected': highlightedIndex === index ? 'true' : 'false',
              'aria-label': item[displayField],
              role: 'option',
              key: item[valueField]
            }
          : {},
      itemProps:
        grouped && !item.category ? { ...getItemProps({ item, index, key: item.id }) } : {},
      props: {
        ...Object.assign(
          {},
          propStyles,
          getItemProps(
            Object.assign(
              {},
              { index, item },
              this.getItemStateProps(highlightedIndex, index, item, propStyles)
            )
          )
        )
      }
    };
  };

  getSelectedItem = () => {
    return this.state.selectedItem;
  };

  _shouldShowDropdown = () => {
    return (
      this.state.manuallyOpened ||
      !this.props.minLength ||
      this.state.inputValue.length >= this.props.minLength
    );
  };
  render = () => {
    const {
      showLabel,
      defaultIsOpen,
      selectedItemChanged,
      icon,
      itemHeight,
      // dropDownMaxHeight,
      onInputValueChange,
      loading,
      disabled,
      placeholder,
      minLength,
      inputItem,
      menuItem,
      noResultItem,
      selectMode,
      labelKey,
      noResultLabelKey,
      displayField,
      showSelectorMenuArrow,
      flatMode,
      showArrows,
      showArrowsIfClearable,
      showLabelInside,
      dataTestId,
      grouped,
      hasError,
      inputFocusReferences,
      showLoadingMessage,
      loadingMessage,
      // isMobile,
      // virtualListHeightSubtraction,
      enableVirtualList,
      customAccessibility,
      enabledAccessibilityLabel = false,
      enableClearInputButton,
      clearInputButtonSettings,
      clearInputIconSettings,
      makeAccessibleByArrowsKeys = false,
      enableSecondarySearch,
      secondarySearchSettings,
      secondarySearchRef,
      footerElement,
      A11yStatusMessageLabelKey = 'cmp-location-selector-a11y-status-message'
    } = this.props;
    const { translate, direction, theme } = this.context;
    const {
      selectedItem,
      clearable,
      inputValue,
      dropDownHeight,
      inputValueSearch,
      inputValueSearchSecondary
    } = this.state;

    const [className, style] = getStyles(this.props, { direction, theme });
    const filter = this.getOptions(inputValueSearch, inputValueSearchSecondary);
    const itemCount = filter.length;

    const ParentDropdownElement = this.props.focusLock ? ReactFocusLock : React.Fragment;

    return (
      <Downshift
        stateReducer={this.stateReducer}
        onStateChange={this.onStateChange}
        itemToString={(item) => item[displayField]}
        defaultIsOpen={defaultIsOpen}
        selectedItemChanged={selectedItemChanged}
        labelId={this.labelId}
        itemCount={itemCount}
        ref={(downshift) => {
          this.downshift = downshift;
        }}
        selectedItem={selectedItem}
        onInputValueChange={onInputValueChange}
        initialHighlightsIndex={this.highlightedIndex}
        defaultHighlightedIndex={this.highlightedIndex}
        getA11yStatusMessage={(info) => {
          if (!info.isOpen) {
            if (this.state.firstResultAnt !== '') {
              this.setState({ firstResultAnt: '' });
            }
            return '';
          } else {
            if (
              this.state.firstResultAnt === info.highlightedItem?.iataCode &&
              info.resultCount > 0
            ) {
              return `${info.highlightedItem?.label} ${strFormat(
                translate(
                  A11yStatusMessageLabelKey,
                  `{0} results are available, use up and down arrow keys to navigate. Press Enter key to select.`
                ),
                [info.resultCount]
              )}`;
            } else if (minLength === 0 && info.highlightedIndex === null && info.resultCount > 0) {
              return `${strFormat(
                translate(
                  A11yStatusMessageLabelKey,
                  `{0} results are available, use up and down arrow keys to navigate. Press Enter key to select.`
                ),
                [info.resultCount]
              )}`;
            }
          }
          if (!info.resultCount) {
            return translate(noResultLabelKey, 'No result to show');
          }
          if (info.resultCount !== info.previousResultCount && minLength <= inputValue.length) {
            return strFormat(
              translate(
                A11yStatusMessageLabelKey,
                `{0} results are available, use up and down arrow keys to navigate. Press Enter key to select.`
              ),
              [info.resultCount]
            );
          }
          return '';
        }}
      >
        {({
          getRootProps,
          getLabelProps,
          getInputProps,
          getItemProps,
          getMenuProps,
          isOpen,
          highlightedIndex
        }) => {
          // const dropDownHeight = isMobile
          //   ? clientHeight() - virtualListHeightSubtraction
          //   : Math.min(itemCount * itemHeight + 50, dropDownMaxHeight); //TODO: I added 20px more so that the elements have a default spacing

          this.wasOpenSelector.current = isOpen;

          const labelProps = getLabelProps();
          let shouldOpen = this.props.enableRightIconDropdown !== false;
          let newMenuProps = getMenuProps();
          let customEvents = !inputItem.ToggleElement;
          if (this.props.customEvents === true) {
            customEvents = false;
          }
          return (
            <div
              style={style}
              data-testid={dataTestId}
              className={`location-selector-container fr-position-relative ${className}`}
            >
              {this.props.useInput ? (
                <>
                  <VisuallyHidden id={`${this.downshift?.id || 'location-selector'}-keyboard-help`}>
                    {translate(
                      'cmp-location-selector-keyboard-shortcuts',
                      'Enter to select highlighted option. Press Arrow Down to open suggestions, Escape to close.'
                    )}
                  </VisuallyHidden>
                </>
              ) : null}
              <LocationSelectorInput
                ref={this.locationSelectorInputRef}
                // onKeyDown travel interest only
                onKeyDown={(e) => {
                  this.props.onKeyDown && this.props.onKeyDown(e);
                }}
                {...Object.assign(
                  {},
                  customEvents &&
                    getInputProps({
                      onBlur: this.onBlur,
                      onKeyDown: (e) => this.onKeyDown(e, filter, highlightedIndex, isOpen)
                    }),
                  inputItem
                )}
                onClick={(e) => {
                  if (inputItem.ToggleElement) {
                    if (!isOpen) {
                      this.setState({ inputValueSearchSecondary: '' });
                      this.downshift.openMenu();
                      setTimeout(() => {
                        secondarySearchRef?.current?.focus();
                      }, 20);
                    } else {
                      this.downshift.closeMenu();
                    }
                  }
                  if (!isOpen && this.props.openOnClick) {
                    this.props.onClickInput(e);
                    this.onOpenMenu();
                  } else if (isOpen && this.props.openOnClick) {
                    this.downshift.closeMenu();
                  }
                }}
                onBlur={() => {
                  if (this.props.closeOnFocusLoss) {
                    this.onCloseMenu();
                  }
                }}
                showEmptyFilterMessage={this.state.showEmptyFilterMessage}
                emptyFilterMessage={translate(
                  `cmp-location-selector-empty-value-${labelKey}`,
                  'Required field'
                )}
                id={labelProps.id}
                rootProps={getRootProps({}, { suppressRefError: true })}
                isOpen={
                  this.props.showToggleButtonLabels === false
                    ? isOpen && !loading
                    : isOpen && this._shouldShowDropdown() && !loading
                }
                isRightIconClickable={shouldOpen}
                onClear={this.onClear}
                onChange={this.handleInputValue}
                focusReferences={inputFocusReferences}
                onCloseMenu={this.onCloseMenu}
                active={inputItem.ToggleElement ? inputItem.active : () => {}}
                onOpenMenu={this.onOpenMenu}
                customClearInputButton={enableClearInputButton}
                onFocus={() => {
                  this.onFocus();
                }}
                dataTestId="locationSelectorInput"
                icon={icon}
                clearable={clearable}
                labelProps={labelProps}
                showArrowsIfClearable={showArrowsIfClearable}
                showArrows={showArrows}
                showErrorLabel={hasError && !isOpen}
                invalid={hasError}
                placeholder={placeholder}
                label={translate(`cmp-location-selector-label-${labelKey}`, labelKey)}
                role="combobox"
                aria-autocomplete="list"
                aria-expanded={isOpen ? 'true' : 'false'}
                aria-haspopup="listbox"
                aria-controls={this.downshift?.id}
                aria-describedby={`${this.downshift?.id || 'location-selector'}-keyboard-help`}
                aria-activedescendant={
                  isOpen &&
                  this.downshift?.id &&
                  highlightedIndex !== null &&
                  this._shouldShowDropdown()
                    ? `${this.downshift.id}-item-${highlightedIndex}`
                    : null
                }
                onKeyUp={(e) => {
                  this.scrollIntoView(highlightedIndex);
                  //onKeyUp travel interest only
                  if (this.props.onKeyUp) {
                    this.props.onKeyUp(e);
                  }
                }}
                onKeyPress={this.onKeyPress}
                onPaste={this.onPaste}
                loading={!showLoadingMessage && loading}
                disabled={disabled}
                value={inputValue}
                readonly={selectMode}
                leftTabIndex={[-1]} // TODO: Because it is defined if inside in the LocationSelectorInput is redefined
                showLabel={showLabel}
                showLabelInside={showLabelInside}
                ariaDescribedBy={`${this.downshift?.id || 'location-selector'}-keyboard-help`}
                ariaActivedescendant={
                  isOpen &&
                  this.downshift?.id &&
                  highlightedIndex !== null &&
                  this._shouldShowDropdown()
                    ? `${this.downshift.id}-item-${highlightedIndex}`
                    : null
                }
                isMobile={this.props.isMobile}
                makeAccessibleByArrowsKeys={makeAccessibleByArrowsKeys}
                useInput={this.props.useInput}
              />
              {(this.props.showToggleButtonLabels === false
                ? isOpen
                : isOpen && this._shouldShowDropdown()) && (
                <ParentDropdownElement
                  {...(this.props.focusLock ? { autoFocus: true, returnFocus: true } : {})}
                >
                  <LocationSelectorMenu
                    {...Object.assign({}, newMenuProps, menuItem, {
                      showArrow: showSelectorMenuArrow,
                      flatMode,
                      useAsDialog: this.props.useAsDialog,
                      ariaLabel: this.props.useAsDialog
                        ? translate(
                            this.props.dialogAriaLabel ?? 'cmp-location-selector-dialog-aria-label',
                            'Select your interests'
                          )
                        : ''
                      // position: isMobile ? 'none' : 'absolute'
                    })}
                  >
                    <>
                      {enableSecondarySearch && (
                        <LocationSelectorSecondarySearch
                          {...secondarySearchSettings}
                          placeholder={translate(
                            'cmp-location-selector-secondary-search-placeholder',
                            'Search'
                          )}
                          label={translate(
                            'cmp-location-selector-secondary-search-label',
                            'Search'
                          )}
                          onClear={() => this.setState({ inputValueSearchSecondary: '' })}
                          onChange={(value) => this.setState({ inputValueSearchSecondary: value })}
                          ref={secondarySearchRef}
                          onKeyUp={this.props.onKeyUp}
                          onCloseMenu={this.onCloseMenu}
                          focusSelectorInput={this.focusSelectorInput}
                          onFocus={() => {
                            this.setState({ wasOpenFirstTime: false });
                            // this.downshift.openMenu();
                          }}
                        />
                      )}
                      {itemCount > 0 ? (
                        enableVirtualList ? (
                          <>
                            {showLoadingMessage && loading ? (
                              <Box {...loadingMessage}>
                                {translate('cmp-label-loading-with-dots', 'Loading...')}
                              </Box>
                            ) : null}
                            <RowVirtualizerDynamic
                              id={this.downshift?.id}
                              dropDownHeight={dropDownHeight}
                              size={itemCount}
                              itemHeight={itemHeight}
                              grouped={grouped}
                              highlightedIndex={highlightedIndex}
                              getItemProps={getItemProps}
                              itemsProps={this.getLocationItemProps}
                              displayField={displayField}
                              items={filter}
                              itemRenderer={this.itemRenderer}
                              customAccessibility={customAccessibility}
                              enableVirtualList={enableVirtualList}
                              getMenuProps={getMenuProps}
                              wasOpenFirstTime={this.state.wasOpenFirstTime}
                              openOnFocus={this.props.openOnFocus}
                              useInput={this.props.useInput}
                              selectedItem={this.state?.selectedItem}
                              notifySelectedState={this.props.notifySelectedState}
                              enabledAccessibilityLabel={enabledAccessibilityLabel}
                            />
                          </>
                        ) : (
                          <>
                            <ol
                              id={this.downshift?.id}
                              role="listbox"
                              className="fr-px-0 fr-ol"
                              tabIndex={-1}
                              {...getMenuProps({
                                id: this.downshift?.id,
                                role: this.props.useInput ? 'listbox' : undefined,
                                style: {
                                  ...(enableVirtualList
                                    ? { margin: '0' }
                                    : {
                                        margin: '0',
                                        display: 'flex',
                                        flexDirection: 'column'
                                      }),
                                  maxHeight: dropDownHeight,
                                  overflowY: 'auto'
                                },
                                tabIndex: -1
                              })}
                            >
                              {showLoadingMessage && loading ? (
                                <Box {...loadingMessage}>
                                  {translate('cmp-label-loading-with-dots', 'Loading...')}
                                </Box>
                              ) : null}
                              {filter.map((item, index) => {
                                const Tag = grouped && item.category ? 'div' : 'li';
                                const locationItemProps = this.getLocationItemProps(
                                  index,
                                  highlightedIndex,
                                  filter,
                                  getItemProps
                                );
                                const getItemPropsSettings = this.props.useInput
                                  ? getItemProps({
                                      key: item.value,
                                      index,
                                      item,
                                      style: {
                                        backgroundColor:
                                          highlightedIndex === index ? 'lightgray' : 'white',
                                        fontWeight: selectedItem === item ? 'bold' : 'normal',
                                        pointerEvents: item.pointerEvents
                                          ? item.pointerEvents
                                          : 'auto'
                                      },
                                      disabled: item.disabled ? item.disabled : false
                                    })
                                  : null;

                                return (
                                  <Tag
                                    {...locationItemProps.attrs}
                                    {...locationItemProps.itemProps}
                                    {...getItemPropsSettings}
                                    aria-selected={
                                      this.props.useInput
                                        ? locationItemProps.props['aria-selected']
                                        : null
                                    }
                                    role={!this.props.useInput ? null : 'option'}
                                  >
                                    {grouped && item.category ? (
                                      <LocationSelectorGroupHeader {...locationItemProps.props}>
                                        {this.itemRenderer(item)}
                                      </LocationSelectorGroupHeader>
                                    ) : (
                                      <LocationSelectorMenuItem
                                        {...locationItemProps.props}
                                        id={`${this.downshift.id}-item-${index}`}
                                        role="option"
                                        ariaLabel={
                                          customAccessibility
                                            ? `${item[displayField]}  ${index + 1} ${translate(
                                                'cmp-location-selector-separator',
                                                'of'
                                              )} ${itemCount}`
                                            : item[displayField]
                                        }
                                        customAccessibility={customAccessibility}
                                        wasOpenFirstTime={this.state.wasOpenFirstTime}
                                        useInput={this.props.useInput}
                                      >
                                        {this.itemRenderer(item)}
                                      </LocationSelectorMenuItem>
                                    )}
                                  </Tag>
                                );
                              })}
                            </ol>
                          </>
                        )
                      ) : (
                        <LocationSelectorNoResult {...noResultItem}>
                          {translate(noResultLabelKey, 'No result to show')}
                        </LocationSelectorNoResult>
                      )}
                      {footerElement && footerElement()}
                    </>
                  </LocationSelectorMenu>
                </ParentDropdownElement>
              )}
              {this.state.inputValue &&
                this.state.inputValue.length !== 0 &&
                !this.props.loading &&
                enableClearInputButton && (
                  <Button
                    position="absolute"
                    color="transparent"
                    display="flex"
                    alignItems="center"
                    style={
                      direction === 'rtl'
                        ? {
                            left: '1px',
                            bottom: '1px',
                            height: '46px',
                            zIndex: 5
                          }
                        : {
                            right: '1px',
                            bottom: '1px',
                            height: '46px',
                            zIndex: 5
                          }
                    }
                    ariaLabel={translate('cmp-clear-button-description', 'Clear filter')}
                    onClick={() => {
                      this.onClear();
                      if (inputFocusReferences && inputFocusReferences?.current) {
                        inputFocusReferences?.current?.focus();
                      }
                    }}
                    onBlur={() => {
                      if (this.props.setCloseButtonFocus) {
                        this.props.setCloseButtonFocus(false);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.keyCode === KEY_ENTER) {
                        this.onClear();
                        if (inputFocusReferences && inputFocusReferences?.current) {
                          inputFocusReferences?.current?.focus();
                        }
                      } else if (e.keyCode === KEY_TAB && !e.shiftKey) {
                        if (this.props.name !== '') {
                          if (this.props.setEnableAriaHiddenFooter) {
                            this.props.setEnableAriaHiddenFooter(false);
                          }
                          if (this.props.name === 'Origin') {
                            if (this.props.setIsClearOriginFocus) {
                              this.props.setIsClearOriginFocus(false);
                            }
                            if (this.props.setIsClearDestinationFocus) {
                              this.props.setIsClearDestinationFocus(false);
                            }
                          } else {
                            if (
                              this.props.setIsClearDestinationFocus ||
                              this.props.setIsClearOriginFocus ||
                              this.props.isClearOriginFocus ||
                              this.props.isClearDestinationFocus ||
                              this.props.setEnableAriaHiddenFooter
                            ) {
                              if (this.props.setIsClearDestinationFocus) {
                                this.props.setIsClearDestinationFocus(false);
                              }
                              if (this.props.setIsClearOriginFocus) {
                                this.props.setIsClearOriginFocus(false);
                              }
                              setTimeout(() => {
                                const tmp = document.querySelectorAll('.footerButtons');
                                if (tmp) {
                                  tmp[0]?.focus();
                                }
                              }, 900);
                            }
                          }
                        }
                      }
                    }}
                    ariaHidden={
                      this.props.name !== ''
                        ? this.props.name === 'Origin'
                          ? this.props.isClearDestinationFocus
                          : this.props.isClearOriginFocus
                        : null
                    }
                    onFocus={() => {
                      if (this.props.name !== '') {
                        if (this.props.setEnableAriaHiddenFooter) {
                          this.props.setEnableAriaHiddenFooter(true);
                        }
                        if (this.props.name === 'Origin') {
                          if (this.props.setIsClearDestinationFocus) {
                            this.props.setIsClearDestinationFocus(false);
                          }
                          if (this.props.setIsClearOriginFocus) {
                            this.props.setIsClearOriginFocus(true);
                          }
                        } else {
                          if (this.props.setIsClearOriginFocus) {
                            this.props.setIsClearOriginFocus(false);
                          }
                          if (this.props.setIsClearDestinationFocus) {
                            this.props.setIsClearDestinationFocus(true);
                          }
                        }
                      }
                      if (this.props.setCloseButtonFocus) {
                        this.props.setCloseButtonFocus(true);
                      }
                    }}
                    {...clearInputButtonSettings}
                  >
                    <Icon icon="close" {...clearInputIconSettings} />
                  </Button>
                )}
            </div>
          );
        }}
      </Downshift>
    );
  };
}

LocationSelector.propTypes = {
  /**
   * Setting of loading message
   */
  loadingMessage: PropTypes.objectOf(PropTypes.any),
  /**
   * Determine if show loading message inside of the input
   */
  showLoadingMessage: PropTypes.bool,
  /**
   * Determine if is use the virtual list
   */
  enableVirtualList: PropTypes.bool,
  /**
   * get the input ref
   */
  inputFocusReferences: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  /**
   * Determine if there are any errors
   */
  hasError: PropTypes.bool,
  /**
   * Define if search trought the options props
   */
  searchInside: PropTypes.bool,
  /**
   * Key of the data object to show in the list
   */
  displayField: PropTypes.string,
  /**
   * Key of the data object to use like id
   */
  valueField: PropTypes.string,
  /**
   * This is the value to set the isOpen to anytime downshift is reset, when the the selection is cleared, or when an item is selected.
   */
  defaultIsOpen: PropTypes.bool,
  /**
   *  Disable or enable component.
   */
  disabled: PropTypes.bool,
  /**
   * Renderer of item
   */
  itemRenderer: PropTypes.func,
  /**
   * Define if autocomplete is loading
   */
  loading: PropTypes.bool,
  /**
   * Define length to search of component
   */
  minLength: PropTypes.number,
  /**
   * Function fired when change selection
   */
  onChange: PropTypes.func,
  /**
   * Function fired when input's value change
   */
  onInputChange: PropTypes.func,
  /**
   * Function fired when clear selection
   */
  onClear: PropTypes.func,
  /**
   * Function fired when write in input
   */
  onInputValueChange: PropTypes.func,
  /**
   * Define if component open when is focused
   */
  openOnFocus: PropTypes.bool,
  /**
   * Define if the component is open when clicked
   */
  openOnClick: PropTypes.bool,
  /**
   * Define options of autocomplete
   */
  options: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)),
  /**
   * Used to determine if the new selectedItem has changed compared to the previous selectedItem and properly update Downshift's internal state.
   */
  selectedItemChanged: PropTypes.func,
  /**
   * Icon to show in the input
   */
  icon: PropTypes.string,
  /**
   * Define if show clear button
   */
  clearable: PropTypes.bool,
  /**
   * Setting of input
   */
  inputItem: PropTypes.objectOf(PropTypes.any),
  /**
   * Setting of menu
   */
  menuItem: PropTypes.objectOf(PropTypes.any),
  /**
   * Each item of list
   */
  listItem: PropTypes.objectOf(PropTypes.any),
  /**
   * Settings of no result
   */
  noResultItem: PropTypes.objectOf(PropTypes.any),
  /**
   * Define if autocomplete is only seleccionable
   */
  selectMode: PropTypes.bool,
  /**
   * Define if it has grouped options
   */
  grouped: PropTypes.bool,
  /**
   * Define if label is show
   */
  showLabel: PropTypes.bool,
  /**
   * Define if label inside is show
   */
  showLabelInside: PropTypes.bool,
  /**
   * Define placeholder
   */
  placeholder: PropTypes.string,
  /**
   * Label Key using for the translate
   */
  labelKey: PropTypes.string,
  /**
   * Label Key using for the translate
   */
  noResultLabelKey: PropTypes.string,
  /**
   * Selected value of the component
   * FIXME: This name is somewhat misleading, `defaultValue` is used as a convention for uncontrolled inputs, but this
   * value is used to fill the input on every update.
   */
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  selectedItems: PropTypes.array,
  /**
   * Define if component show up and down arrows if component clearable
   */
  showArrowsIfClearable: PropTypes.bool,
  /**
   * Grouped all labels in selectedItems or not
   */
  multipleSelectionGroupLabel: PropTypes.bool,
  /**
   * enable multi selection in locationSelector
   */
  multipleSelection: PropTypes.bool,
  /**
   * Define if component show up and down arrows
   */
  showArrows: PropTypes.bool,
  /**
   * onFocus
   */
  onFocus: PropTypes.func,
  /**
   * onBlur
   */
  onBlur: PropTypes.func,
  onClickInput: PropTypes.func,
  /**
   * Define if arrow in menu show or hide
   */
  showSelectorMenuArrow: PropTypes.bool,
  /**
   * Define if component have a max-height
   */
  flatMode: PropTypes.bool,
  /**
   * Setting of group in menu
   */
  groupItem: PropTypes.objectOf(PropTypes.any),
  /**
   * onOpen
   */
  onOpen: PropTypes.func,
  /**
   * Data Test ID
   */
  dataTestId: PropTypes.string,
  /**
   * Max height of the dropdown list
   */
  dropDownMaxHeight: PropTypes.number,
  /**
   * Height of a single dropdown item
   */
  itemHeight: PropTypes.number,
  /**
   * define the characters allowed when the user is typing
   */
  charactersAllowed: PropTypes.oneOf(['all', 'onlyLetters', 'onlyNumbers', 'lettersAndNumbers']),
  isMobile: PropTypes.bool,
  virtualListHeightSubtraction: PropTypes.number,
  forceOpenDesktopInMobile: PropTypes.bool,
  customAccessibility: PropTypes.bool,
  labelKeySeparatorMultiSelection: PropTypes.string,
  useInput: PropTypes.bool,
  customEvents: PropTypes.bool,
  slowCloseAction: PropTypes.bool,
  name: PropTypes.string,
  showLabelCollapsedMenssage: PropTypes.bool,
  allowVariableHighlightedIndex: PropTypes.bool,
  notifySelectedState: PropTypes.bool,
  focusLock: PropTypes.bool,
  useAsDialog: PropTypes.bool,
  /**
   * Enable or disable the dropdown opening when clicking on the right icon.
   * When true (default): right icon is clickable and focusable.
   * When false: right icon is not clickable or focusable, but keyboard Arrow Down still works.
   * @default true
   */
  enableRightIconDropdown: PropTypes.bool
};
LocationSelector.defaultProps = {
  enableVirtualList: true,
  name: '',
  showLoadingMessage: false,
  hasError: false,
  searchInside: true,
  displayField: 'label',
  valueField: 'value',
  searchOptions: {
    // https://fusejs.io/api/options.html
    isCaseSensitive: false,
    includeScore: false,
    includeMatches: false,
    minMatchCharLength: 1,
    shouldSort: true,
    findAllMatches: false,
    keys: ['iataCode', 'label'],
    location: 0,
    threshold: 0,
    distance: 10,
    ignoreLocation: false
  },
  defaultIsOpen: false,
  disabled: false,
  itemRenderer: null,
  grouped: false,
  dataTestId: 'locationSelector',
  loading: false,
  loadingMessage: {},
  minLength: 0,
  onChange: () => {},
  onInputChange: () => {},
  onClear: () => {},
  onInputValueChange: () => {},
  onClickInput: () => {},
  openOnFocus: false,
  openOnClick: false,
  options: [],
  selectedItemChanged: () => {},
  inputFocusReferences: React.createRef(),
  secondarySearchRef: React.createRef(),
  icon: null,
  clearable: false,
  inputItem: {},
  menuItem: {},
  listItem: {},
  noResultItem: {},
  selectMode: false,
  showLabel: false,
  showLabelInside: false,
  placeholder: '',
  labelKey: 'origin',
  noResultLabelKey: 'cmp-location-noresult',
  defaultValue: null,
  showArrowsIfClearable: true,
  onFocus: () => {},
  showSelectorMenuArrow: true,
  flatMode: false,
  showArrows: true,
  groupItem: {},
  onBlur: () => {},
  onOpen: () => {},
  itemHeight: 37,
  dropDownMaxHeight: 400,
  charactersAllowed: 'all',
  isMobile: false,
  virtualListHeightSubtraction: 160,
  forceOpenDesktopInMobile: false,
  customAccessibility: false,
  multipleSelectionGroupLabel: true,
  useInput: true,
  labelKeySeparatorMultiSelection: 'cmp-separator-comma',
  customEvents: false,
  slowCloseAction: false,
  showLabelCollapsedMenssage: false,
  allowVariableHighlightedIndex: false,
  selectOptionByDefault: true,
  notifySelectedState: true,
  forceBlur: false, // Force blur false by default
  focusLock: false,
  useAsDialog: false,
  enableRightIconDropdown: true
};

LocationSelector.contextType = Context;

LocationSelector.componentName = 'locationSelector';

export default LocationSelector;
